import { C, staffNames } from "@bd/content";
import { BALANCE } from "./balance";
import { priceIndex, yearOf } from "./calendar";
import { defaultDesign, mechThemeFit } from "./design";
import { suggestGameName } from "./names";
import type { Company, Design, GameState, Project } from "./state";
import { projectWork } from "./company";
import { expectedFirstYear } from "./sales";
import { isUnlocked } from "./unlocks";
import { log, money, newId, rng } from "./util";

/** A game an outside designer brings you: sign it for an advance and a royalty, and it starts well along. */
export interface Pitch {
  id: string;
  designer: string;
  /** 1–5: how good, and how far along, the design is */
  stars: number;
  design: Design;
  advance: number;
  /** their share of every sale */
  royalty: number;
  expires: number;
}

/**
 * What the designer asks up front: a share of what the game should make you in its first year
 * (20%, plus 4% a star), and never less than a small floor.
 */
export function pitchAdvance(state: GameState, co: Company, design: Design, stars: number): number {
  const head = pitchHeadStart(stars);
  const p = { id: "pitch", design, phase: "ready", work: projectWork({ design, startedWeek: state.week } as Project, state.week), playtests: [], known: [], fixes: [], fixing: [], startedWeek: state.week, hype: 0, marketing: 0, preorders: 0, crunch: false, kind: "base" } as Project;
  for (const ph of Object.values(p.work)) {
    ph.done = ph.need;
    ph.quality = head.quality;
  }
  const revenue = expectedFirstYear(state, co, p) * design.price * 0.45;
  return Math.round(Math.max(BALANCE.pitchBase * priceIndex(state.week), revenue * (0.2 + 0.04 * stars)));
}

/** How far along a signed pitch starts: the prototype mostly done, some art; and how good that work is. */
export function pitchHeadStart(stars: number): { prototype: number; art: number; quality: number } {
  return { prototype: Math.min(0.95, 0.45 + stars * 0.09), art: 0.1 + stars * 0.04, quality: Math.min(0.9, 0.5 + stars * 0.07) };
}

/** Every half year or so, once you're out of the garage, a designer pitches you a game. */
export function pitchesWeek(state: GameState, co: Company): void {
  co.pitches = (co.pitches ?? []).filter((p) => p.expires > state.week);
  if (co.level === "garage" || yearOf(state.week) < 1969) return;
  const every = 26;
  if (state.week % every !== (co.id.length * 5) % every || co.pitches.length >= 2) return;
  const r = rng(state, `pitch:${co.id}`);
  // better designers come to better-known publishers
  const stars = Math.max(1, Math.min(5, Math.round(1.4 + (co.reputation / 100) * 2.2 + r.normal(0, 1))));
  const mechs = co.unlocked.filter((u) => u.startsWith("m:")).map((u) => u.slice(2));
  const themes = co.unlocked.filter((u) => u.startsWith("t:")).map((u) => u.slice(2));
  if (!mechs.length || !themes.length) return;
  // a designer brings a pairing they believe in: one of the better fits among a few they tried
  let best = { m: mechs[0], t: themes[0], fit: -1 };
  for (let i = 0; i < 6 + stars * 2; i++) {
    const m = r.pick(mechs);
    const t = r.pick(themes);
    const fit = mechThemeFit(m, t);
    if (fit > best.fit) best = { m, t, fit };
  }
  const mech = C.mechanic[best.m];
  const auds = Object.entries(mech.audience).filter(([a]) => isUnlocked(co, `a:${a}`)).sort((a, b) => (b[1] as number) - (a[1] as number));
  const audience = (auds[0]?.[0] ?? "family") as Design["audience"];
  const format = mech.formats.find((f) => isUnlocked(co, `f:${f}`)) ?? "card";
  const design = defaultDesign(suggestGameName(co, best.t, state.week), best.m, best.t, audience, format, state.week);
  const order = ["garage", "office", "studio", "hq"];
  design.scope = stars >= 4 && order.indexOf(co.level) >= 2 && yearOf(state.week) >= 1985 ? "epic" : stars >= 3 && order.indexOf(co.level) >= 1 ? "big" : "standard";
  const name = `${r.pick(staffNames.first)} ${r.pick(staffNames.last)}`;
  const pitch: Pitch = { id: newId(state, "pt"), designer: name, stars, design, advance: pitchAdvance(state, co, design, stars), royalty: Math.round((0.05 + stars * 0.025) * 100) / 100, expires: state.week + 16 };
  co.pitches = [...co.pitches, pitch];
  log(state, "pitch", `${name} (${"★".repeat(stars)}) pitches ${design.name}: ${mech.name} × ${C.theme[best.t].name}. Advance ${money(pitch.advance)}, ${Math.round(pitch.royalty * 100)}% royalty.`, co.id);
}
